import { create } from "zustand";
import { persist } from "zustand/middleware";
import convex, { waitForConvexAuthReady } from "../convexClient";
import { api } from "../convex/_generated/api";

/**
 * Manages the active tenant (workspace) for the authenticated user.
 *
 * allTenants  – every workspace the user belongs to
 * tenantId    – Convex ID of the active workspace
 * tenantSlug  – URL slug of the active workspace (used in paths)
 * tenantName  – display name of the active workspace
 * role        – the user's role in the active workspace
 */
export const useTenantStore = create(
  persist(
    (set, get) => ({
      allTenants: [],
      tenantId: null,
      tenantSlug: null,
      tenantName: null,
      role: null,
      loading: false,
      error: null,

      /** Fetch all tenants the user belongs to and pick the first (or keep current). */
      fetchUserTenants: async () => {
        set({ loading: true, error: null });
        try {
          await waitForConvexAuthReady();
          const tenants = await convex.query(api.tenants.getUserTenants, {});
          const current = get().tenantId;
          // Keep the active tenant if it's still in the list; else pick first.
          const active =
            tenants.find((t) => String(t.id) === String(current)) ||
            tenants[0] ||
            null;

          set({
            allTenants: tenants,
            tenantId: active?.id || null,
            tenantSlug: active?.slug || null,
            tenantName: active?.name || null,
            role: active?.role || null,
            loading: false,
          });

          return { tenants, active };
        } catch (err) {
          console.error("[useTenantStore] fetchUserTenants:", err);
          set({ loading: false, error: err.message });
          return { tenants: [], active: null };
        }
      },

      /** Set an already-fetched tenant as active. */
      setActiveTenant: (tenant) => {
        if (!tenant) return;
        set({
          tenantId: tenant.id || tenant._id,
          tenantSlug: tenant.slug,
          tenantName: tenant.name,
          role: tenant.role || null,
        });
      },

      /** Update allTenants list after adding a new tenant. */
      addTenant: (tenant) => {
        set((state) => ({ allTenants: [...state.allTenants, tenant] }));
      },

      /** Clear all tenant state (on sign-out). */
      clearTenant: () => {
        set({
          allTenants: [],
          tenantId: null,
          tenantSlug: null,
          tenantName: null,
          role: null,
        });
      },
    }),
    {
      name: "tenant-store",
      partialize: (state) => ({
        tenantId: state.tenantId,
        tenantSlug: state.tenantSlug,
        tenantName: state.tenantName,
        role: state.role,
      }),
    }
  )
);
